import { nimbusSchedulingConfig } from "@/configuration/groups/nimbus/scheduling/config";
import { type AutoIntakeConfig } from "@/src/lib/auto-intake/auto-intake-config";
import { AutoIntakeBlockCheck } from "@/src/lib/auto-intake/auto-intake-eligibility-requirements";
import { HolidayNames } from "@/src/lib/utils/holidays";
import { AppointmentType } from "@/src/models/appointment";
import { Classification, ClassificationCategory, ClassificationSection } from "@/src/models/constants/classifications";
import { Workflow, WorkflowType } from "@/src/models/constants/group";
import { ClassificationPageDict } from "@/src/models/file";
import { Group } from "@/src/models/group";

export const classificationToNimbusMap = {
  [ClassificationCategory.CLASSIFICATION]: [
    Classification.CONSENT,
    Classification.IDENTIFICATION_CARD,
    Classification.INSURANCE_CARD,
    Classification.INTAKE,
    Classification.COVER_PAGE,
  ],
  [ClassificationCategory.LAB_RESULTS]: [Classification.LAB_RESULTS],
  [ClassificationCategory.MEDICAL_HISTORY]: [Classification.MEDICAL_HISTORY],
  [ClassificationCategory.IMAGING_RESULTS]: [
    Classification.IMAGING_RESULTS_CT,
    Classification.IMAGING_RESULTS_X_RAY,
    Classification.IMAGING_RESULTS_ECHO,
    Classification.IMAGING_RESULTS_EKG,
    Classification.IMAGING_RESULTS_PULMONARY_FUNCTION,
    Classification.IMAGING_RESULTS_OTHER,
  ],
  [ClassificationCategory.PROGRESS_NOTE]: [Classification.PROGRESS_NOTE],
  [ClassificationCategory.PROCEDURE]: [Classification.PROCEDURE_SLEEP, Classification.CLEARANCE_REQUEST],
  [ClassificationCategory.REFERRAL]: [
    Classification.REFERRAL,
    Classification.REFERRAL_STATUS_REQUEST,
    Classification.REFERER_INFORMATION,
  ],
  [ClassificationCategory.ADMIN]: [
    Classification.ADMIN_DISABILITY_FORMS,
    Classification.MEDICAL_RECORD_REQUEST,
    Classification.MEDICAL_RECORD_REQUEST_HEDIS,
  ],
  [ClassificationCategory.PHARMACY_RELATED]: [Classification.PHARMACY_RELATED],
  [ClassificationCategory.PRIOR_AUTHORIZATION]: [Classification.PRIOR_AUTHORIZATION],
  [ClassificationCategory.DME_ORDER]: [Classification.DME_ORDER],
};

/**
 * Returns the classifications to display for selection for the Nimbus group
 *
 * @returns The classifications to display for selection for the Nimbus group
 */
export function getNimbusClassificationsToDisplayForSelection(): ClassificationSection[] {
  const classificationMap = Object.entries(classificationToNimbusMap).map(([label, options]) => {
    return {
      label,
      options: options.map((option) => option.toLowerCase()),
    };
  });

  return classificationMap;
}

export function getNimbusClassificationsToProcess(
  classificationPageDict: ClassificationPageDict
): ClassificationPageDict {
  if (Classification.REFERRAL in classificationPageDict) {
    return { [Classification.REFERRAL]: classificationPageDict[Classification.REFERRAL] };
  }

  return classificationPageDict;
}

const NIMBUS_HOLIDAYS = [
  HolidayNames.NEW_YEARS_DAY,
  HolidayNames.THANKSGIVING,
  HolidayNames.BLACK_FRIDAY,
  HolidayNames.CHRISTMAS_EVE,
  HolidayNames.CHRISTMAS_DAY,
] as const;

/**
 * Returns the holidays for the Nimbus group
 *
 * @returns The holidays for the Nimbus group
 */
export function getNimbusHolidays(): HolidayNames[] {
  return [...NIMBUS_HOLIDAYS];
}

/**
 * Returns the workflows for the Nimbus group
 *
 * @param group - The group to get the workflows for
 * @returns The workflows for the Nimbus group
 */
export async function getNimbusWorkflows(group: Group): Promise<Partial<Record<WorkflowType, Classification[]>>> {
  return {
    [Workflow.REFERRAL]: [Classification.REFERRAL],
  };
}

export function getNimbusUploadClassification(classification: Classification): Classification {
  switch (classification) {
    case Classification.COVER_PAGE:
    case Classification.INSURANCE_CARD:
    case Classification.INTAKE:
    case Classification.IDENTIFICATION_CARD:
      return Classification.REFERRAL;
    default:
      return classification;
  }
}

const NIMBUS_BUNDLE_TO_REFERRAL_CLASSIFICATIONS = new Set<Classification>([
  Classification.COVER_PAGE,
  Classification.INSURANCE_CARD,
  Classification.INTAKE,
  Classification.IDENTIFICATION_CARD,
]);

/**
 * Returns the classifications to extract for the Nimbus group
 *
 * @returns The classifications to extract for the Nimbus group
 */
export function getNimbusClassificationToExtract(): Classification[] {
  return Object.values(classificationToNimbusMap)
    .flat()
    .filter((c) => !NIMBUS_BUNDLE_TO_REFERRAL_CLASSIFICATIONS.has(c));
}

/**
 * Returns the classifications for document upload for the Nimbus group
 *
 * @returns The classifications for document upload for the Nimbus group
 */
export function getNimbusClassificationForDocumentUpload(): Classification[] {
  return Object.values(classificationToNimbusMap).flat();
}

export function getNimbusScheduleConfigPath(): string {
  return nimbusSchedulingConfig.policy.scheduleConfigPath;
}

interface NimbusAppointmentTypeMapping {
  openTemplateAppointmentTypeId: number;
  newPatientAppointmentTypeId: number;
  establishedPatientAppointmentTypeId: number;
}

enum NimbusAppointmentTypeId {
  Any20 = 1,
  EstablishedPatient20 = 3,
  NewPatient40 = 4,
  HospitalFollowUp20 = 7,
  Any15 = 41,
  EstablishedPatient15 = 43,
  NewPatient30 = 45,
  HospitalFollowUp15 = 48,
  HospitalFollowUp30 = 49,
}

const NIMBUS_40_20_APPOINTMENT_TYPES: NimbusAppointmentTypeMapping = {
  openTemplateAppointmentTypeId: NimbusAppointmentTypeId.Any20,
  newPatientAppointmentTypeId: NimbusAppointmentTypeId.NewPatient40,
  establishedPatientAppointmentTypeId: NimbusAppointmentTypeId.EstablishedPatient20,
};

const NIMBUS_30_15_APPOINTMENT_TYPES: NimbusAppointmentTypeMapping = {
  openTemplateAppointmentTypeId: NimbusAppointmentTypeId.Any15,
  newPatientAppointmentTypeId: NimbusAppointmentTypeId.NewPatient30,
  establishedPatientAppointmentTypeId: NimbusAppointmentTypeId.EstablishedPatient15,
};

export enum NimbusDepartmentEhrId {
  CamelbackOffice = "4",
  ColumbusOffice = "58",
  EasleyOffice = "34",
  EatontonOffice = "43",
  MaconOffice = "2",
  MaconPruittHealthSnf = "19",
  PrescottOffice = "5",
  RockHillOffice = "1",
  SunCityWestOffice = "3",
}

export const NIMBUS_DEPARTMENT_NAMES: Record<NimbusDepartmentEhrId, string> = {
  [NimbusDepartmentEhrId.RockHillOffice]: "SC - Rock Hill - Office",
  [NimbusDepartmentEhrId.MaconOffice]: "GA - Macon - Office",
  [NimbusDepartmentEhrId.SunCityWestOffice]: "AZ - Sun City West - Office",
  [NimbusDepartmentEhrId.CamelbackOffice]: "AZ - Camelback - Office",
  [NimbusDepartmentEhrId.PrescottOffice]: "AZ - Prescott - Office",
  [NimbusDepartmentEhrId.MaconPruittHealthSnf]: "GA - Macon - PruittHealth - SNF",
  [NimbusDepartmentEhrId.EasleyOffice]: "SC - Easley - Office",
  [NimbusDepartmentEhrId.EatontonOffice]: "GA - Eatonton - Office",
  [NimbusDepartmentEhrId.ColumbusOffice]: "GA - Columbus - Office",
};

export function getNimbusDepartmentIds(): string[] {
  return Object.keys(NIMBUS_DEPARTMENT_NAMES);
}

export function getNimbusDepartmentName(departmentId: string): string | null {
  return NIMBUS_DEPARTMENT_NAMES[departmentId as NimbusDepartmentEhrId] ?? null;
}

const NIMBUS_DEPARTMENT_APPOINTMENT_TYPES: Record<NimbusDepartmentEhrId, NimbusAppointmentTypeMapping> = {
  [NimbusDepartmentEhrId.RockHillOffice]: NIMBUS_40_20_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.MaconOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.SunCityWestOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.CamelbackOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.PrescottOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.MaconPruittHealthSnf]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.EasleyOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.EatontonOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
  [NimbusDepartmentEhrId.ColumbusOffice]: NIMBUS_30_15_APPOINTMENT_TYPES,
};

export function getNimbusAppointmentTypeId(isNew: boolean, departmentId: string): number {
  const mapping = NIMBUS_DEPARTMENT_APPOINTMENT_TYPES[departmentId as NimbusDepartmentEhrId];

  if (!mapping) {
    throw new Error(`No Nimbus appointment type mapping for Athena department ${departmentId}`);
  }

  return isNew ? mapping.newPatientAppointmentTypeId : mapping.establishedPatientAppointmentTypeId;
}

const NIMBUS_BOOKED_APPOINTMENT_TYPES: Partial<Record<NimbusAppointmentTypeId, AppointmentType>> = {
  [NimbusAppointmentTypeId.EstablishedPatient20]: AppointmentType.FOLLOW_UP,
  [NimbusAppointmentTypeId.NewPatient40]: AppointmentType.NEW_PATIENT_CHECKIN,
  [NimbusAppointmentTypeId.EstablishedPatient15]: AppointmentType.FOLLOW_UP,
  [NimbusAppointmentTypeId.NewPatient30]: AppointmentType.NEW_PATIENT_CHECKIN,
  [NimbusAppointmentTypeId.HospitalFollowUp20]: AppointmentType.FOLLOW_UP,
  [NimbusAppointmentTypeId.HospitalFollowUp15]: AppointmentType.FOLLOW_UP,
  [NimbusAppointmentTypeId.HospitalFollowUp30]: AppointmentType.FOLLOW_UP,
};

/**
 * Map Nimbus's authoritative Athena booked appointment-type IDs back to the
 * internal type used by confirmation and rescheduling. Generic ANY15/ANY20
 * templates are intentionally excluded because they do not encode visit intent.
 */
export function getNimbusAppointmentTypeFromBookedEhrId(
  _providerEhrId: string,
  athenaAppointmentTypeId: number
): AppointmentType | null {
  return NIMBUS_BOOKED_APPOINTMENT_TYPES[athenaAppointmentTypeId as NimbusAppointmentTypeId] ?? null;
}

const NIMBUS_APPOINTMENT_TYPES_BY_OPEN_TYPE_ID: Partial<Record<NimbusAppointmentTypeId, NimbusAppointmentTypeMapping>> =
  {
    [NimbusAppointmentTypeId.Any20]: NIMBUS_40_20_APPOINTMENT_TYPES,
    [NimbusAppointmentTypeId.EstablishedPatient20]: NIMBUS_40_20_APPOINTMENT_TYPES,
    [NimbusAppointmentTypeId.NewPatient40]: NIMBUS_40_20_APPOINTMENT_TYPES,
    [NimbusAppointmentTypeId.Any15]: NIMBUS_30_15_APPOINTMENT_TYPES,
    [NimbusAppointmentTypeId.EstablishedPatient15]: NIMBUS_30_15_APPOINTMENT_TYPES,
    [NimbusAppointmentTypeId.NewPatient30]: NIMBUS_30_15_APPOINTMENT_TYPES,
  };

/**
 * Athena can return the generic schedule template that backs a Nimbus slot
 * instead of the requested booked visit type. Map that authoritative open-slot
 * type back to the visit type that must be sent when the slot is filled.
 */
export function getNimbusBookedTypeIdFromOpenTypeId(
  providerEhrId: string,
  openAppointmentTypeId: string,
  internalAppointmentType: AppointmentType | null
): number {
  const openTypeId = Number(openAppointmentTypeId) as NimbusAppointmentTypeId;
  const mapping = NIMBUS_APPOINTMENT_TYPES_BY_OPEN_TYPE_ID[openTypeId];

  if (!mapping) {
    throw new Error(
      `No Nimbus booked appointment type mapping for Athena open type ${openAppointmentTypeId} from provider ${providerEhrId}`
    );
  }

  const isNew =
    internalAppointmentType === AppointmentType.INITIAL_CONSULTATION ||
    internalAppointmentType === AppointmentType.NEW_PATIENT_CHECKIN;
  const bookedTypeId = isNew ? mapping.newPatientAppointmentTypeId : mapping.establishedPatientAppointmentTypeId;
  const genericOpenTypeId = mapping.openTemplateAppointmentTypeId;

  if (openTypeId !== genericOpenTypeId && openTypeId !== bookedTypeId) {
    throw new Error(
      `Athena open appointment type ${openAppointmentTypeId} is not valid for Nimbus provider ${providerEhrId}`
    );
  }

  return bookedTypeId;
}

export const NIMBUS_AUTO_INTAKE_CONFIG: AutoIntakeConfig = {
  byClassification: {
    // Gates are scoped to this policy rather than the group-level `blockChecks` so they only
    // constrain referrals, and each is pinned to a specific block key: REFERRAL also enables
    // primaryCareProvider and referringProvider, so the block-type checks
    // (MATCHED_PROVIDER_NPI / GEOCODED_LOCATION) would demand an NPI on those too.
    //
    // Deliberately no `requiredBlocks.anyOf`: an `anyOf` group exempts its blocks from the
    // per-block required-field gate, and referrals must satisfy every enabled block's
    // requirements before they can close unreviewed.
    [Classification.REFERRAL]: {
      rate: 0.75,
      blockChecks: [
        AutoIntakeBlockCheck.MATCHED_RECEIVING_PROVIDER_NPI,
        AutoIntakeBlockCheck.GEOCODED_RECEIVING_LOCATION,
        AutoIntakeBlockCheck.GEOCODED_REFERRING_LOCATION,
      ],
    },
    [Classification.COVER_PAGE]: { rate: 0.5 },
    [Classification.DME_ORDER]: { rate: 0.5 },
    [Classification.PROCEDURE_SLEEP]: { rate: 0.5 },
    [Classification.REFERRAL_STATUS_REQUEST]: { rate: 0.5 },
  },
};
